import type {
  FrontDeskOverview,
  FrontDeskRow,
} from "../bookings/frontDeskTypes";
import { UNPAID_AT_VENUE_LABEL, unpaidAtVenue } from "../../shared/payments/unpaidAtVenue";
import { money } from "../../shared/money";
import { nextSteps, type NextStepAction } from "../bookings/nextSteps";
import { bookingStatusLabel } from "../bookings/bookingStatus";
import type { CreatedBy } from "../bookings/frontDeskTypes";
import { T } from "../../shared/labels";

/** Local midnight → next local midnight, as ISO (the backend filters [from, to)). */
export function localDayRange(day: Date): { from: string; to: string } {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

/** Tomorrow in local time — the call list window. */
export function localNextDayRange(day: Date): { callFrom: string; callTo: string } {
  const { from, to } = localDayRange(new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1));
  return { callFrom: from, callTo: to };
}

export type FrontDeskTabKey = "today" | "awaiting" | "callList" | "changeRequests" | "refunds";

export function frontDeskTabs(
  o: FrontDeskOverview,
): Array<{
  key: FrontDeskTabKey;
  label: string;
  count: number;
  alert: boolean;
}> {
  return [
    { key: "today", label: "Өнөөдөр", count: o.today.length, alert: false },
    {
      key: "awaiting",
      label: "Төлбөр хүлээж буй",
      count: o.awaitingPayment.length,
      alert: o.counts.overduePayments > 0,
    },
    {
      key: "callList",
      label: "Залгах жагсаалт",
      count: o.callList.length,
      alert: o.counts.callList > 0,
    },
    {
      key: "changeRequests",
      label: "Цаг өөрчлөх хүсэлт",
      count: o.changeRequests.length,
      alert: o.counts.changeRequests > 0,
    },
    {
      key: "refunds",
      label: "Буцаалт өгөх",
      count: o.refundsOwed.length,
      alert: o.counts.refundsOwed > 0,
    },
  ];
}

/**
 * Row buttons: the same rules as the booking detail. Front-desk rows carry
 * the classified hold / refund instead of raw settings, so rebuild the bits
 * `nextSteps` reads.
 */
/** The booking's full `nextSteps`, from what a front-desk row carries. */
function rowSteps(row: FrontDeskRow, now: Date): NextStepAction[] {
  const settings: Record<string, unknown> = {};
  if (row.awaitingPayment) settings.source = "admin_console";
  if (row.refundOwed) settings.refundOwed = { amount: row.refundOwed.amount };
  return nextSteps({ status: row.status, startTime: row.startTime, settings, paymentSummary: row.paymentSummary, now });
}

export function rowQuickActions(
  row: FrontDeskRow,
  now: Date,
  tab: FrontDeskTabKey = "today",
): NextStepAction[] {
  if (tab === "callList") return ["MARK_CALLED", "RESCHEDULE"];
  if (tab === "changeRequests") return ["RESCHEDULE", "RESOLVE_CHANGE_REQUEST"];
  return rowSteps(row, now)
    // The call list tab owns «Залгасан»; rows here don't know `callHandled`.
    .filter((a) => a !== "MARK_CALLED")
    .slice(0, 3);
}

/** What a stylist may do from the kiosk; the rest stays at the front desk. */
const KIOSK_ACTIONS: ReadonlySet<NextStepAction> = new Set<NextStepAction>([
  "MARK_CAME",
  "MARK_NO_SHOW",
  "RECORD_PAYMENT",
  "CONFIRM_WITHOUT_PAYMENT",
]);

/**
 * Kiosk «Миний захиалга»: every step the booking has (not the today tab's
 * single desk step — "came and paid" is the main kiosk case), kept to the
 * kiosk's actions, no length cut.
 */
export function kioskRowActions(row: FrontDeskRow, now: Date): NextStepAction[] {
  return rowSteps(row, now).filter((a) => KIOSK_ACTIONS.has(a));
}

/** Front-desk payment column (C4 §8 money format). */
export function paymentCell(r: Pick<FrontDeskRow, "paymentSummary"> & { status?: string }): string {
  const { price, paid, balance } = r.paymentSummary;
  if (price === 0) return "Үнэгүй";
  if (r.status && unpaidAtVenue({ status: r.status, paymentSummary: r.paymentSummary })) return UNPAID_AT_VENUE_LABEL;
  if (price !== null && (balance ?? 0) === 0) return "Төлсөн";
  return price === null ? money(paid, { free: false }) : `${money(paid, { free: false })} / ${money(price, { free: false })}`;
}

/** Browser-local HH:MM, like the rest of the front desk. */
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const byStart = (rows: FrontDeskRow[]): FrontDeskRow[] =>
  [...rows].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

export function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export type Tone = "ok" | "warn" | "danger" | "quiet";

/** The one tag a time line row shows — money and attendance, most urgent first. */
export function rowTag(row: FrontDeskRow): { label: string; tone: Tone } {
  const { price, paid, balance } = row.paymentSummary;
  if (row.refundOwed) return { label: `Буцаах ${money(row.refundOwed.amount, { free: false })}`, tone: "danger" };
  if (row.awaitingPayment?.overdue) return { label: "Хугацаа хэтэрсэн", tone: "danger" };
  if (row.awaitingPayment) return { label: `Шилжүүлэг ${hhmm(row.awaitingPayment.payBy)}`, tone: "warn" };
  if (row.status === "COMPLETED") return { label: "Ирсэн", tone: "ok" };
  if (row.status === "NO_SHOW") return { label: "Ирээгүй", tone: "danger" };
  if (price === 0) return { label: "Үнэгүй", tone: "quiet" };
  if (unpaidAtVenue({ status: row.status, paymentSummary: row.paymentSummary })) {
    return { label: UNPAID_AT_VENUE_LABEL, tone: "warn" };
  }
  if ((balance ?? 0) > 0) return { label: "Үлдэгдэл", tone: "warn" };
  if (price !== null && paid >= price) return { label: "Төлсөн", tone: "ok" };
  return { label: bookingStatusLabel(row.status), tone: "quiet" };
}

const UPCOMING = new Set(["PENDING", "CONFIRMED"]);

/** Today's next customer: the first live booking that has not ended yet. */
export function nextCustomer(
  rows: FrontDeskRow[],
  now: Date,
  isToday: boolean,
): { row: FrontDeskRow; label: string } | null {
  if (!isToday) return null;
  const row = byStart(rows).find((r) => UPCOMING.has(r.status) && new Date(r.endTime).getTime() > now.getTime());
  if (!row) return null;
  const ms = new Date(row.startTime).getTime() - now.getTime();
  if (ms <= 0) return { row, label: "Одоо явагдаж байна" };
  if (ms < 60_000) return { row, label: "Одоо эхэлнэ" };
  if (ms < 3_600_000) return { row, label: `Дараагийнх · ${Math.floor(ms / 60_000)} минутын дараа` };
  return { row, label: `Дараагийнх · ${hhmm(row.startTime)}` };
}

/** Where «Одоо» goes in the start-sorted list: after every row that has started. Null off today. */
export function nowLineIndex(rows: FrontDeskRow[], now: Date, isToday: boolean): number | null {
  if (!isToday) return null;
  return rows.filter((r) => new Date(r.startTime).getTime() <= now.getTime()).length;
}

/** Short counter words that fit four across a phone (the full label is the button's name). */
export const COUNTER_LABELS: Record<FrontDeskTabKey, string> = {
  today: "Өнөөдөр",
  awaiting: "Төлбөр",
  callList: "Залгах",
  changeRequests: "Өөрчлөх",
  refunds: "Буцаалт",
};

/**
 * The next-customer card's buttons: «Ирсэн», then taking the money, then
 * confirming a hold leads; «Төлбөр» sits beside it when it isn't the lead;
 * the rest go in «⋯».
 */
export function cardActions(actions: NextStepAction[]): {
  main: NextStepAction | null;
  pay: boolean;
  more: NextStepAction[];
} {
  const hasPay = actions.includes("RECORD_PAYMENT");
  const lead = (["MARK_CAME", "RECORD_PAYMENT", "CONFIRM_WITHOUT_PAYMENT"] as const).find((a) => actions.includes(a));
  const main = lead ?? actions[0] ?? null;
  return {
    main,
    pay: hasPay && main !== "RECORD_PAYMENT",
    more: actions.filter((a) => a !== main && a !== "RECORD_PAYMENT"),
  };
}

/** Steps worth a button on a compact row; the rest live in the booking detail. */
const DESK_STEPS: ReadonlySet<NextStepAction> = new Set<NextStepAction>(["MARK_CAME", "RECORD_PAYMENT", "CONFIRM_WITHOUT_PAYMENT", "SETTLE_REFUND"]);

/** Row buttons on the time line: today, the one desk step (plus «Ирээгүй» beside «Ирсэн»); the other lists show every step. */
export function timelineRowActions(row: FrontDeskRow, now: Date, tab: FrontDeskTabKey): NextStepAction[] {
  const all = rowQuickActions(row, now, tab);
  if (tab !== "today") return all;
  const { main } = cardActions(all);
  if (!main || !DESK_STEPS.has(main)) return [];
  // After the start the desk settles attendance both ways: «Ирсэн» and «Ирээгүй».
  return main === "MARK_CAME" && all.includes("MARK_NO_SHOW") ? [main, "MARK_NO_SHOW"] : [main];
}

/** «Анужин Б · Киоск» — who made the booking; null when there is nothing to say. */
export function createdByText(createdBy: CreatedBy | null | undefined): string | null {
  if (!createdBy) return null;
  const parts = [createdBy.name?.trim() || null, createdBy.source === "kiosk" ? T.kiosk : null].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** «Бүртгэсэн: Анужин Б · Киоск» for a front-desk row. */
export function createdByLine(createdBy: CreatedBy | null | undefined): string | null {
  const text = createdByText(createdBy);
  return text ? `${T.createdBy}: ${text}` : null;
}

